// AccountProfile — the person's own identity section, pure.
//
// The world is always authenticated (Stream B removed the local no-auth
// world), so the identity IS the sign-in email:
//   - sign-in       → READ-ONLY, the real authenticated email (never a device
//     display-name standing in for it). Before the `/auth/me` probe resolves
//     the identity is `pending` and this row shows a quiet "signing in" note,
//     never a faked email.
//   - display name  → editable per-device PREFERENCE (Input; reports every
//     change UP, consumer commits — apply-on-change, see Settings/types). It
//     is what shows on authored materials, not your identity.
//
// Account vs IAM: this is strictly "my identity for myself". Managing OTHER
// users / workspace members lives in the IAM surface (components/Iam/), never
// here.
//
// Composition: SettingsSection (the calm card) + SettingsRow (each labelled
// pairing) + Input. Register (.claude/research/design/06): near-monochrome; the email and
// pending note read on the ink ramp.
//
// Pure/props-driven: identity arrives as a prop; the panel authors its own
// captions via Lingui and holds no state. Every change is reported UP.

import { useLingui } from "@lingui/react/macro";
import { Input } from "../../Input/Input.js";
import { Text } from "../../Text/Text.js";
import { SettingsRow } from "../SettingsRow/SettingsRow.js";
import { SettingsSection } from "../SettingsSection/SettingsSection.js";
import type { AccountIdentity } from "../types.js";

export type AccountProfileProps = {
    readonly identity: AccountIdentity;
    // Reports each display-name edit UP; the consumer decides when to commit.
    readonly onDisplayNameChange: (value: string) => void;
    // The workspace name (F56): the RAW value ("" = unnamed) shown in the
    // field, with the derived display default as its placeholder. Each edit
    // reported UP; the consumer dispatches the `renameWorkspace` command.
    readonly workspaceName: string;
    readonly workspaceNamePlaceholder: string;
    readonly onWorkspaceNameChange: (value: string) => void;
};

const NAME_FIELD_ID = "account-display-name";
const WORKSPACE_FIELD_ID = "account-workspace-name";

export const AccountProfile = ({
    identity,
    onDisplayNameChange,
    workspaceName,
    workspaceNamePlaceholder,
    onWorkspaceNameChange,
}: AccountProfileProps) => {
    const { t } = useLingui();
    return (
        <SettingsSection
            title={t`Account`}
            description={t`Who you are in this app.`}
        >
            <SettingsRow
                label={t`Signed in as`}
                description={t`Your identity in this workspace.`}
                control={
                    identity.type === "authenticated" ? (
                        <Text size="sm">{identity.email}</Text>
                    ) : (
                        <Text tone="dim" size="sm">
                            {t`Signing in…`}
                        </Text>
                    )
                }
            />

            <SettingsRow
                label={t`Display name`}
                description={t`Shown on the materials you author.`}
                htmlFor={NAME_FIELD_ID}
                control={
                    <Input
                        id={NAME_FIELD_ID}
                        value={identity.displayName}
                        placeholder={t`Your name`}
                        onChange={(e) => onDisplayNameChange(e.target.value)}
                        aria-label={t`Display name`}
                    />
                }
            />

            <SettingsRow
                label={t`Workspace name`}
                description={t`The name of this workspace.`}
                htmlFor={WORKSPACE_FIELD_ID}
                control={
                    <Input
                        id={WORKSPACE_FIELD_ID}
                        value={workspaceName}
                        placeholder={workspaceNamePlaceholder}
                        onChange={(e) => onWorkspaceNameChange(e.target.value)}
                        aria-label={t`Workspace name`}
                    />
                }
            />
        </SettingsSection>
    );
};
